import {
  BarElement,
  CategoryScale,
  ChartData,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import { useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import DataTable, { TableColumn } from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import ApplicationsFilter, {
  ApplicationFilterState,
} from "../../components/ApplicationsFilter/ApplicationsFilter";
import { Dialog } from "../../components/CommonComponents/Dialog/Dialog";
import { PaginatorForRDT } from "../../components/CommonComponents/Paginator/Paginator";
import SortIcon from "../../components/CommonComponents/SortIcon/SortIcon";
import { tableCustomStyles } from "../../components/CommonComponents/Table/Table";
import { isUserOrArchitect } from "../../hoc/isUserOrArchitect";
import {
  TApplication,
  useApplicationCounts,
  useApplicationStats,
  useApplications,
  useDeleteApplication,
} from "../../hooks/applications.hooks";
import { useToggle } from "../../hooks/common";
import { formatDate } from "../../utils/date";
import { DeleteButton, ViewButton } from "../../components/CommonComponents/EditComponent/EditComponent";
import BoxComponent from "../../components/CommonComponents/BoxComponent/BoxComponent";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const labels = ["January", "February", "March", "April", "May", "June", "July"];

const options = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    title: {
      display: true,
      padding: 32,
      font: { size: 24 },
      text: "Applications",
    },
  },
  scales: {
    x: {
      title: {
        display: true,
        text: "Application Status",
        font: { size: 20 },
        padding: 16,
      },
    },
    y: {
      ticks: {
        stepSize: 1,
      },
      title: {
        display: true,
        text: "Number of Applications",
        font: { size: 20 },
        padding: 16,
      },
    },
  },
};

export const UserDashboard = isUserOrArchitect(() => {
  const navigate = useNavigate();
  const [filterOpen, toggleFilter] = useToggle();
  const [view, setView] = useState<"chart" | "table">("chart");
  const [page, setPage] = useState(1);
  const [{ sortKey, sortOrder }, setSort] = useState({
    sortKey: "submittedAt",
    sortOrder: -1,
  });
  const [filterState, setFilterState] = useState<ApplicationFilterState>({
    connector: "and",
    conditions: [],
  });
  const { mutate: deleteApplication, isPending: deletingApplication } =
    useDeleteApplication();
  const { data } = useApplicationStats({ filters: filterState });
  const labels = (data?.data || []).map((e) => e.status);

  const { isLoading: isLoading1, data: counts } = useApplicationCounts({
    filters: filterState,
  });
  const { isLoading: isLoading2, data: listData } = useApplications({
    page,
    limit: 10,
    sort: sortKey,
    sortVal: sortOrder,
    filters: filterState,
  });

  const chartData: ChartData<"bar", number[], string> = {
    labels,
    datasets: [
      {
        data: (data?.data || []).map((e) => e.count),
        barThickness: 45,
        backgroundColor: "rgba(53, 162, 235,1)",
      },
    ],
  };

  const columns = useMemo(
    (): TableColumn<TApplication>[] => [
      {
        name: "Application ID",
        width: "180px",
        sortable: true,
        sortField: "applicationId",
        selector: (d) => d.applicationId,
        center: true,
        grow: 2,
      },
      {
        name: "Plot Owner's Name",
        sortable: true,
        sortField: "plot_owner_s_name",
        // selector: (d) => d.plot_owner_s_name || "-",
        cell: (d) => (
          <span className="text-center">{d.plot_owner_s_name || "-"}</span>
        ),
        center: true,
        grow: 3,
      },
      {
        name: "Status",
        width: "280px",
        center: true,
        sortable: true,
        sortField: "status",
        cell: (d) =>
          d.status?.startsWith("Revoked") ? (
            <span className="text-center font-semibold text-xs px-2.5 py-1 rounded-full bg-red-100 text-red-700 border border-red-300">
              {d.status}
            </span>
          ) : (
            <span className="text-center">{d.status || "-"}</span>
          ),
        grow: 3,
      },
      {
        name: "Date of Submission",
        width: "235px",
        center: true,
        sortable: true,
        sortField: "submittedAt",
        cell: (d) => (d.submittedAt ? formatDate(d.submittedAt) : "-"),
        grow: 2,
      },
      {
        name: 'Created On',
        selector: (row: any) => formatDate(new Date(row.createdAt)),
        center: true,
        sortable: true,
        width: "170px",
      },
      {
        name: 'Modified On',
        selector: (row: any) => formatDate(new Date(row.updatedAt)),
        center: true,
        sortable: true,
        width: "170px",
      },
      {
        name: "Action",
        grow: 0.5,
        center: true,
        cell: (e) => (
          // <button
          //   onClick={() => {
          //     if (e.status === "Draft") {
          //       navigate("/edit-application", { state: { id: e.id } });
          //     } else navigate("/view-application", { state: { id: e.id } });
          //   }}
          //   className="icon-button"
          // >
          //   <i className="m-0 fa fa-eye"></i>
          // </button>
          <div className="flex space-x-2">
            {/* <ViewButton
              onClick={() => {
                if (e.status === "Draft") {
                  navigate("/edit-application", { state: { id: e.id } });
                } else navigate("/view-application", { state: { id: e.id } });
              }}
            /> */}
            <ViewButton
              onClick={() => {
                if (e.status === "Draft") {
                  navigate("/edit-application", { state: { id: e.id } });
                } else navigate("/view-application", { state: { id: e.id } });
              }}
            />
            <DeleteButton
              onClick={() => deleteApplication(e.id)}
              className={
                e.status === "Draft" ? "" : "pointer-events-none opacity-0"
              }
              disabled={!(e?.status === 'Draft')}
            />
          </div>
        ),
      },
    ],
    [navigate]
  );

  return (
    <>
      <div className="p-4 w-full box-border">
        <h4 className="text-xl sm:text-2xl font-bold pb-4 ">Dashboard</h4>
        <BoxComponent>
          <div className="p-2 sm:p-4 md:p-4 md:pt-2 bg-white">
            <div className="flex justify-end mb-4">
              {/* <h4 className="text-xl sm:text-2xl font-bold">Dashboard</h4> */}
              <div className="flex space-x-4">
                <button
                  className="p-3 bg-white border border-[#968f8f] rounded-xl flex justify-center items-center"
                  onClick={() => {
                    setView((e) => (e === "chart" ? "table" : "chart"));
                    setPage(1);
                  }}
                >
                  {view === "table" ? (
                    <i className="fa fa-bar-chart text-primary m-0" />
                  ) : (
                    <i className="fa fa-list-ul text-primary m-0" />
                  )}
                </button>
                <button
                  className="p-3 bg-white border border-[#968f8f] rounded-xl flex justify-center items-center min-w-[42px]"
                  onClick={toggleFilter}
                >
                  <i className="fa fa-filter text-primary m-0" />
                </button>
              </div>
            </div>
            {view === "chart" ? (
              <div
                className="chart-container overflow-x-auto"
                style={{ position: "relative", height: "75vh", width: "100%" }}
              >
                <div style={{ minWidth: "900px", height: "100%" }}>
                  <Bar options={options} data={chartData} />
                </div>
              </div>
            ) : (
              <DataTable
                pagination
                paginationServer
                className="mt-4"
                columns={columns}
                sortIcon={<SortIcon />}
                data={listData?.data || []}
                customStyles={tableCustomStyles}
                onChangePage={(p) => setPage(p)}
                paginationTotalRows={counts?.data || 0}
                progressPending={isLoading1 || isLoading2}
                paginationComponent={PaginatorForRDT}
                onSort={(col, dir) => {
                  if (!col?.sortField) return;
                  setSort({
                    sortKey: col.sortField || "",
                    sortOrder: dir === "asc" ? 1 : -1,
                  });
                }}
              />
            )}
          </div>
        </BoxComponent>
      </div>
      <Dialog
        onClose={toggleFilter}
        open={filterOpen}
        className="flex flex-col sm:w-[600px] lg:w-[800px]"
      >
        <ApplicationsFilter
          onClose={toggleFilter}
          defaultFilter={filterState}
          onChange={setFilterState}
        />
      </Dialog>
    </>
  );
});
